import { useEffect, useRef } from "react";

const PLAID_LINK_SCRIPT_URL = process.env.REACT_APP_PLAID_LINK_SCRIPT_URL;

let plaidScriptPromise = null;

function loadPlaidScript(scriptUrl) {
    if (window.Plaid) {
        return Promise.resolve(window.Plaid);
    }

    if (plaidScriptPromise) {
        return plaidScriptPromise;
    }

    if (!scriptUrl) {
        return Promise.reject(new Error("Missing REACT_APP_PLAID_LINK_SCRIPT_URL"));
    }

    plaidScriptPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${scriptUrl}"]`);

        if (existing) {
            existing.addEventListener("load", () => resolve(window.Plaid));
            existing.addEventListener("error", () =>
                reject(new Error("Failed to load Plaid script"))
            );
            return;
        }

        const script = document.createElement("script");
        script.src = scriptUrl;
        script.async = true;

        script.onload = () => resolve(window.Plaid);
        script.onerror = () => {
            plaidScriptPromise = null;
            reject(new Error("Failed to load Plaid script"));
        };

        document.head.appendChild(script);
    });

    return plaidScriptPromise;
}

export default function usePlaidLink({
    linkToken,
    mode = "modal",
    embeddedContainerId,
    onSuccess,
    onExit,
    onEvent,
    onLoad,
    onError,
}) {
    const handlerRef = useRef(null);
    const hasOpenedRef = useRef(false);
    const isMountedRef = useRef(true);

    const onSuccessRef = useRef(onSuccess);
    const onExitRef = useRef(onExit);
    const onEventRef = useRef(onEvent);
    const onLoadRef = useRef(onLoad);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onSuccessRef.current = onSuccess;
        onExitRef.current = onExit;
        onEventRef.current = onEvent;
        onLoadRef.current = onLoad;
        onErrorRef.current = onError;
    }, [onSuccess, onExit, onEvent, onLoad, onError]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!linkToken) {
            return undefined;
        }

        let isEffectActive = true;
        hasOpenedRef.current = false;

        loadPlaidScript(PLAID_LINK_SCRIPT_URL)
            .then((Plaid) => {
                if (!isEffectActive || !isMountedRef.current) {
                    return;
                }

                if (handlerRef.current && handlerRef.current.destroy) {
                    handlerRef.current.destroy();
                }

                handlerRef.current = null;

                const config = {
                    token: linkToken,
                    onSuccess: (publicToken, metadata) => {
                        if (onSuccessRef.current) {
                            onSuccessRef.current(publicToken, metadata);
                        }
                    },
                    onExit: (err, metadata) => {
                        if (onExitRef.current) {
                            onExitRef.current(err, metadata);
                        }
                    },
                    onEvent: (eventName, metadata) => {
                        if (onEventRef.current) {
                            onEventRef.current(eventName, metadata);
                        }
                    },
                    onLoad: () => {
                        if (onLoadRef.current) {
                            onLoadRef.current();
                        }
                    },
                };

                if (mode === "embedded") {
                    const container = document.getElementById(embeddedContainerId);

                    if (!container) {
                        throw new Error(
                            `Plaid embedded container "${embeddedContainerId}" was not found`
                        );
                    }

                    container.innerHTML = "";

                    handlerRef.current = Plaid.createEmbedded(config, container);
                    return;
                }

                handlerRef.current = Plaid.create(config);

                if (!hasOpenedRef.current) {
                    hasOpenedRef.current = true;
                    handlerRef.current.open();
                }
            })
            .catch((err) => {
                if (isEffectActive && isMountedRef.current && onErrorRef.current) {
                    onErrorRef.current(err);
                }
            });

        return () => {
            isEffectActive = false;

            if (handlerRef.current && handlerRef.current.destroy) {
                handlerRef.current.destroy();
            }

            handlerRef.current = null;

            if (mode === "embedded" && embeddedContainerId) {
                const container = document.getElementById(embeddedContainerId);

                if (container) {
                    container.innerHTML = "";
                }
            }
        };
    }, [linkToken, mode, embeddedContainerId]);
}